/* eslint-disable sort-keys-fix/sort-keys-fix */
import Logo from './Logo';

// BASICS
export const baseColors = {
  black: '#000',
  citrus: '#99cc00',
  darkGreen: '#2a6221',
  darkPurple: '#4D5170',
  gray: [
    '#fff', // 0 index
    '#ececec',
    '#dedede',
    '#979797',
    '#312a25',
  ],
  green: '#367c2b',
  lightGreen: ' #dcebbd',
  purple: '#595E88',
  red: '#f00101',
  white: '#fff',
  yellow: '#ffcc32',
};

export const colors = {
  ...baseColors,
  accent: baseColors.citrus,
  danger: baseColors.red,
  dark: baseColors.gray[4],
  darkGray: baseColors.gray[4],
  info: baseColors.green,
  lightGray: baseColors.gray[2],
  muted: baseColors.gray[1],
  primary: baseColors.green,
  primaryWhite: baseColors.white,
  secondary: baseColors.lightGreen,
  success: baseColors.green,
  tertiary: baseColors.citrus,
  text: baseColors.gray[4],
  textMuted: baseColors.gray[3],
  warning: baseColors.yellow,
};

export const fonts = {
  body: "'Helvetica Neue', Helvetica, 'Encode Sans', sans-serif",
  heading: "'Helvetica Neue', Helvetica, 'Encode Sans', sans-serif",
};

export const fontWeights = {
  body: 400,
  bold: 700,
  display: 300,
  heading: 400,
  headingAlt: 600,
  light: 300,
  normal: 400,
};

export const fontSizes = {
  0: '0.75rem',
  1: '0.875rem',
  2: '1rem',
  3: '1.25rem',
  4: '1.625rem',
  5: '1.875rem',
  6: '2rem',
  7: '2.1875rem',
  8: '2.25rem',
  9: '2.5rem',
  10: '2.8125rem',
  11: '3.125rem',
};

export const lineHeights = {
  body: 1.5,
  heading: 1.5,
  paragraph: 1.75,
};

export const sizes = {
  lg: '1150px',
  md: '720px',
  sm: '540px',
  xl: '1440px',
};

export const radii = {
  default: '0.25rem',
  lg: '0.3rem',
  pill: '50rem',
  sm: '0.2rem',
};

export const shadows = {
  default: '0 .5rem 1rem rgba(0, 0, 0, .15)',
  lg: '0 1rem 3rem rgba(0, 0, 0, .175)',
  sm: '0 .125rem .25rem rgba(0, 0, 0, .075)',
};

const heading = {
  fontFamily: fonts.heading,
  fontWeight: fontWeights.heading,
  lineHeight: lineHeights.heading,
};

const display = {
  fontWeight: fontWeights.display,
  lineHeight: lineHeights.heading,
};

// variants
const typeStyles = {
  display,
  heading,
};

export const text = {
  caps: {
    letterSpacing: '0.2em',
    textTransform: 'uppercase',
  },
  heading: {
    color: colors.text,
    fontFamily: fonts.heading,
    fontWeight: fontWeights.heading,
    lineHeight: lineHeights.heading,
    textTransform: 'uppercase',
  },
};

export const styles = {
  a: {
    '&:hover': {
      color: colors.tertiary,
    },
    color: colors.primary,
    textDecoration: 'underline',
  },
  blockquote: {
    backgroundColor: colors.tertiary,
    color: colors.text,
    fontStyle: 'normal',
    fontWeight: fontWeights.body,
  },
  h1: {
    ...heading,
    fontSize: { desktop: fontSizes[11], mobile: fontSizes[10], tablet: fontSizes[8] },
    fontWeight: 700,
    lineHeight: { desktop: '4.125rem', mobile: '3.5rem', tablet: '2.5rem' },
  },
  h2: {
    ...heading,
    fontSize: { desktop: fontSizes[9], mobile: fontSizes[7], tablet: fontSizes[6] },
    fontWeight: 700,
    lineHeight: { desktop: '2.6875rem', mobile: '2.375rem', tablet: '2.25rem' },
  },
  h3: {
    ...heading,
    fontSize: { desktop: fontSizes[5], mobile: fontSizes[4], tablet: fontSizes[3] },
    fontWeight: 700,
    lineHeight: { desktop: '2.1875rem', mobile: '1.875rem', tablet: '1.625rem' },
  },
  h4: {
    ...heading,
    fontSize: { desktop: fontSizes[3], mobile: fontSizes[3], tablet: fontSizes[3] },
    fontWeight: 700,
    lineHeight: { desktop: '1.5rem', mobile: '1.375rem', tablet: '1.375rem' },
  },
  h5: {
    ...heading,
    fontSize: { desktop: fontSizes[2], mobile: fontSizes[2], tablet: fontSizes[2] },
    fontWeight: 700,
    lineHeight: { desktop: '1.25rem', mobile: '1.25rem', tablet: '1.25rem' },
  },
  highlight: {
    backgroundColor: colors.primary,
    color: colors.white,
  },
  img: {
    height: 'auto',
    maxWidth: '100%',
  },
  inlineCode: {
    color: colors.darkGray,
  },
  li: {
    bulletBorderRadius: '10px',
    bulletColor: colors.lightGreen,
  },
  p: {
    fontSize: '1.125rem',
    fontWeight: 400,
    lineHeight: '1.75rem',
    margin: '0',
    marginBottom: '1.7rem',
  },
  root: {
    backgroundColor: colors.white,
    fontFamily: fonts.body,
    fontSize: '1.125rem',
    fontWeight: fontWeights.body,
    lineHeight: lineHeights.body,
  },
  rteHighlight: {
    hoverColor: colors.secondary,
    color: colors.primary,
    fontWeight: '600',
    textDecoration: 'underline',
    isFooter: colors.white,
  },
  table: {
    borderCollapse: 'collapse',
    color: colors.gray[3],
    marginBottom: '3rem',
    width: '100%',
  },
  td: {
    borderBottomColor: colors.gray[3],
    borderBottomStyle: 'solid',
    borderBottomWidth: 2,
    padding: '.75rem',
    verticalAlign: 'top',
  },
  th: {
    borderBottomColor: colors.gray[3],
    borderBottomStyle: 'solid',
    borderBottomWidth: '2px',
    borderTopColor: colors.gray[3],
    borderTopStyle: 'solid',
    borderTopWidth: '2px',
    padding: '.75rem',
    textAlign: 'inherit',
    verticalAlign: 'bottom',
  },
};

const buttonDefaults = {
  borderRadius: '31px',
  cursor: 'pointer',
  fontFamily: fonts.body,
  fontSize: fontSizes[2],
  fontWeight: fontWeights.bold,
  height: '3.625rem',
  minWidth: '194px',
  transition:
    'background-color 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1), transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), color 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
};

export const buttons = {
  disabled: {
    ...buttonDefaults,
    backgroundColor: colors.muted,
    color: colors.textMuted,
    cursor: 'default',
  },
  primary: {
    ...buttonDefaults,
    '&:hover': {
      backgroundColor: colors.darkPurple,
      borderColor: colors.darkPurple,
      color: colors.white,
    },
    backgroundColor: colors.purple,
    borderColor: colors.purple,
    borderStyle: 'solid',
    borderWidth: '1px',
    color: colors.white,
  },
  'primary-with-background': {
    ...buttonDefaults,
    '&:hover': {
      backgroundColor: colors.white,
      color: colors.purple,
    },
    backgroundColor: colors.white,
    borderColor: colors.white,
    borderStyle: 'solid',
    borderWidth: '1px',
    color: colors.primary,
  },
  secondary: {
    ...buttonDefaults,
    '&:hover': {
      backgroundColor: colors.darkGreen,
      borderColor: colors.darkGreen,
      color: colors.white,
    },
    backgroundColor: colors.primary,
    borderColor: colors.primary,
    borderStyle: 'solid',
    borderWidth: '1px',
    color: colors.white,
  },
  tertiary: {
    ...buttonDefaults,
    '&:hover': {
      backgroundColor: colors.primary,
      borderColor: colors.primary,
      color: colors.white,
    },
    backgroundColor: colors.white,
    borderColor: colors.purple,
    borderStyle: 'solid',
    borderWidth: '1px',
    color: colors.primary,
  },
};

// COMPONENTS
export const adviceCardModule = {
  backgroundColor: colors.primary,
};

export const articleDetail = {
  introduction: colors.text,
  tags: {
    backgroundColor: colors.primary,
    color: colors.tertiary,
  },
  title: colors.primary,
};

export const articleListing = {
  background: colors.primary,
  filter: colors.text,
  hover: colors.darkGreen,
  mobile: {
    title: colors.text,
    text: colors.text,
    background: colors.tertiary,
  },
  text: colors.white,
  title: colors.text,
};

export const breadcrumb = {
  backgroundColor: colors.secondary,
};

export const cardCtas = {
  arrowColor: colors.green,
  arrowHoverColor: colors.darkGreen,
  color: colors.green,
  fontSize: '0.75rem',
  hoverColor: colors.darkGreen,
  letterSpacing: '2px',
  lineHeight: '13px',
  textTransform: 'uppercase',
};

export const cards = {
  article: {
    description: colors.text,
    link: {
      '&:hover': {
        color: colors.accent,
        opacity: 1,
        textDecoration: 'underline',
      },
      color: colors.primary,
    },
    title: colors.text,
  },
  logo: {
    color: colors.text,
  },
};

export const carousel = {
  controls: {
    regular: {
      backgroundColor: colors.white,
      color: colors.primary,
      hover: {
        backgroundColor: colors.citrus,
        color: colors.text,
      },
    },
    hero: {
      backgroundColor: colors.tertiary,
      color: colors.text,
      stroke: colors.tertiary,
      opacity: '0.8',
      hover: {
        backgroundColor: colors.tertiary,
        color: colors.text,
        stroke: colors.tertiary,
      },
    },
  },
  indicator: colors.green,
  link: {
    '&:hover': {
      color: colors.secondary,
      opacity: 1,
      textDecoration: 'underline',
    },
    color: colors.primary,
  },
  stroke: 'transparent',
  text: colors.text,
};

export const categoryCards = {
  backgroundColor: colors.green,
};

export const categorySection = {
  background: colors.primary,
  boxShadow: null,
  chevron: {
    fill: colors.tertiary,
    stroke: colors.text,
  },
  color: colors.white,
  date: colors.accent,
  dateColor: colors.text,
  headingColor: colors.text,
};

export const columns = {
  imageCard: colors.text,
  imageCardTitle: colors.primary,
  infoBlock: {
    background: colors.secondary,
    description: colors.text,
    title: colors.text,
  },
};

export const contactForm = {
  heading: {
    color: colors.primary,
  },
};

export const contacts = {
  office: {
    mapTextColor: colors.primary,
    mapTextFontSize: '0.875rem',
    mapTextFontWeight: 'normal',
    mapTextLetterSpacing: '2.33px',
    mapTextLineHeight: '1.71',
    mapTextTransform: 'uppercase',
    openingHoursFontSize: '1rem',
  },
  description: colors.text,
  title: colors.primary,
};

export const cookieBanner = {
  backgroundColor: colors.primary,
};

export const ctaBanner = {
  focusImage: {
    backgroundColor: colors.primary,
    color: colors.white,
    titleColor: colors.white,
  },
  focusText: {
    backgroundColor: colors.accent,
    color: colors.text,
    titleColor: colors.text,
  },
};

export const ctaGroup = {
  chevron: {
    opacity: 1,
  },
  title: colors.text,
};

export const customBlockCards = {
  backgroundColor: colors.accent,
  color: colors.primary,
};

export const dealer = {
  textColor: colors.primary,
};

export const documentDownload = {
  arrowColor: colors.green,
  arrowHoverColor: colors.darkGreen,
  color: colors.green,
  hoverColor: colors.darkGreen,
};

export const footer = {
  activeColor: colors.lightGreen,
  backgroundColor: colors.white,
  color: colors.white,
  minHeight: '160px',
};

export const formInputs = {
  activeColor: colors.lightGreen,
  borderRadius: '31px',
  inputPadding: '0.625rem 1.625rem',
  label: {
    fontSizes: ['12px', null, '16px'],
    lineHeights: ['21px', null, '26px'],
  },
  radioInputBorderRadius: '50%',
  radioSpanBorderRadius: '50%',
  textareaPadding: '1rem 1.625rem',
};

export const header = {
  activeColor: colors.lightGreen,
  backgroundColor: colors.white,
  color: colors.darkGray,
};

export const headerBottom = {
  activeColor: colors.white,
  activeFontWeight: 'bold',
  backgroundColor: colors.green,
  color: colors.white,
  fontSize: '12px',
  fontWeight: 'normal',
};

export const heroBannerButton = {
  'No Background': {
    '&:hover': {
      backgroundColor: colors.darkGreen,
      border: 'none',
      color: colors.white,
    },
    backgroundColor: colors.primary,
    borderRadius: '24px',
    color: colors.white,
    justifyContent: 'center',
    minHeight: '42px',
    padding: '13px 35px 12px',
  },
  'White Background': {
    '&:hover': {
      backgroundColor: colors.darkPurple,
      border: 'none',
      color: colors.white,
    },
    backgroundColor: colors.purple,
    borderRadius: '24px',
    color: colors.white,
    justifyContent: 'center',
    minHeight: '42px',
    padding: '13px 35px 12px',
  },
  arrow: {
    display: 'none',
  },
};

export const heroSection = {
  backgroundColor: colors.primary,
  descriptionColor: colors.white,
  textAlign: 'center',
  titleColor: colors.white,
  titleFontWeight: '700',
  padding: ['20px', '30px', null, '60px'],
  marginTop: ['20px', '20px', null, '48px'],
};

export const imageAndText = {
  color: colors.primary,
};

export const infoBlock = {
  background: colors.tertiary,
  description: colors.text,
  title: colors.primary,
};

export const infoSection = {
  backgroundColor: colors.primary,
  color: colors.white,
  title: colors.white,
};

export const ingredients = {
  backgroundColor: colors.primary,
  color: colors.white,
  hoverColor: colors.darkGreen,
};

export const media = {
  backgroundColor: colors.accent,
  titleFontWeight: 'bold',
  titleTextTransform: 'normal',
};

export const mobileNavigation = {
  arrowColor: colors.lightGreen,
  chevronColor: colors.lightGreen,
};

export const navItems = {
  fontSize: '0.75rem',
  letterSpacing: '2px',
};

export const newsListing = {
  titleFontWeight: 'normal',
};

export const product = {
  backgroundColor: colors.secondary,
  color: colors.text,
};

export const productListing = {
  titleFontWeight: 'normal',
};

export const salesPerson = {
  borderColor: colors.primary,
};

export const search = {
  activeColor: colors.lightGreen,
  borderRadius: '20px',
};

export const shortBanner = {
  backgroundColor: colors.primary,
  color: colors.white,
  titleColor: colors.white,
  titleFontWeight: 'bold',
};

export const table = {
  headerBackgroundColor: colors.primary,
  headerColor: colors.white,
  scrollbarColor: colors.secondary,
};

export const upComingEvents = {
  backgroundColor: colors.primary,
  color: colors.white,
};

export default {
  adviceCardModule,
  articleDetail,
  articleListing,
  breadcrumb,
  buttons,
  cardCtas,
  cards,
  carousel,
  categoryCards,
  categorySection,
  colors,
  columns,
  contactForm,
  contacts,
  cookieBanner,
  ctaBanner,
  ctaGroup,
  customBlockCards,
  dealer,
  documentDownload,
  fontSizes,
  fonts,
  fontWeights,
  footer,
  formInputs,
  header,
  headerBottom,
  heroBannerButton,
  heroSection,
  imageAndText,
  infoBlock,
  infoSection,
  ingredients,
  lineHeights,
  logo: Logo,
  media,
  mobileNavigation,
  name: 'Alliance',
  navItems,
  newsListing,
  product,
  productListing,
  radii,
  salesPerson,
  search,
  shadows,
  shortBanner,
  sizes,
  styles,
  table,
  text,
  typeStyles,
  upComingEvents,
};
